import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import logoNero from '../../assets/logo/Secondario-nero.svg';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  interface NavLink {
    name: string;
    path: string;
  }

  const navLinks: NavLink[] = [
    { name: 'Home', path: '/' },
    { name: 'Chi sono', path: '/about' },
    { name: 'Metodo', path: '/method' },
    { name: 'Servizi', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Contatti', path: '/contact' },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-slate-50/90 backdrop-blur-md border-b border-gray-200/80 py-3 shadow-xs'
          : 'bg-slate-50/90 backdrop-blur-md border-b border-gray-200/60 md:bg-transparent md:border-transparent py-3.5 md:py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <motion.img
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              src={logoNero}
              alt="Matteo Petix — Consulenza IT & Sviluppo Software"
              width="147"
              height="32"
              className="h-8 sm:h-9 w-auto object-contain transition-opacity duration-200 group-hover:opacity-90"
            />
          </Link>

          {/* Desktop Navigation Links - Centered Pill Style */}
          <nav className={`hidden md:flex items-center gap-1 px-3 py-1.5 rounded-full border transition-all duration-300 ${
            isScrolled
              ? 'bg-white/80 backdrop-blur-md border-gray-200/80 shadow-2xs'
              : 'bg-white/50 backdrop-blur-sm border-gray-200/60 shadow-2xs'
          }`}>
            {navLinks.map((link) => {
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-1.5 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap ${
                    active
                      ? 'text-[#4601FA] bg-[#4601FA]/10'
                      : 'text-gray-700 hover:text-black hover:bg-gray-100/70'
                  }`}
                >
                  <span>{link.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* CTA Button & Mobile Toggle */}
          <div className="flex items-center gap-3">
            <motion.div
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="hidden sm:block"
            >
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-4.5 py-2 rounded-full text-xs sm:text-sm font-bold text-black bg-[#ADFE03] hover:bg-[#9ee802] border border-black/10 transition-all duration-200 shadow-2xs hover:shadow-xs group"
              >
                <span>Prenota Call</span>
                <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </motion.div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-xl bg-white/80 border border-gray-200 text-gray-800 hover:bg-gray-100 active:scale-95 transition-all"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden absolute top-full left-0 right-0 h-[calc(100dvh-100%)] overflow-y-auto bg-slate-50/98 backdrop-blur-xl border-b border-gray-200/90 px-4 pt-3 pb-6 shadow-xl"
          >
            <div className="max-w-md mx-auto flex flex-col h-full pb-8">
              <div className="space-y-2">
                {navLinks.map((link) => {
                  const active = isActive(link.path);
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`flex items-center justify-between px-4 py-4 rounded-xl text-[15px] font-semibold transition-all ${
                        active
                          ? 'bg-[#4601FA] text-white shadow-xs'
                          : 'text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <span>{link.name}</span>
                      <ArrowUpRight className="w-5 h-5 opacity-70" />
                    </Link>
                  );
                })}
              </div>

              <div className="mt-auto pt-6">
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 w-full py-4 px-4 rounded-xl text-base font-bold text-black bg-[#ADFE03] hover:bg-[#9ee802] transition-all shadow-xs"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Prenota una Consulenza</span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
